import React, { useCallback, useContext, useMemo, useState } from 'react'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import styled from 'styled-components'
import { rem } from 'polished'
import { formatDate, getOccupancyDescription } from 'components/SearchV2/Utils/GlobalSearchV2'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/LocationSearchFloatingPanel/LocationFloatingPanel'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/DateFloatingPanel'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import { UNIQUE_STAYS_TYPEAHEAD_TYPES } from 'constants/search'
import GlobalSearchQuickFiltersContainer from 'components/Search/GlobalSearch/QuickFilters/GlobalSearchQuickFiltersContainer'
import { StyledGlobalSearchQuickFilterButtonsContainer } from 'components/Search/GlobalSearch/QuickFilters/GlobalSearchQuickFilterButtonsContainer'
import PopularButtonList from 'components/Search/SearchForm/PopularButtonList'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useGlobalVillasAndUniqueStaysTrendingDestinations } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalVillasAndUniqueStaysSearch'
import { connect } from 'react-redux'
import TravellersFloatingPanel from 'components/SearchV2/Components/Mobile/TravellersSearchFloatingPanel/TravellersFloatingPanel'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
     margin-top: ${rem(8)}
    }
  }
`

const PopularSearchesContainer = styled(GlobalSearchQuickFiltersContainer)`
  margin-top: ${rem(24)};
`

const PopularSearchesButtonsWrapper = styled(StyledGlobalSearchQuickFilterButtonsContainer)`
  margin-top: ${rem(8)};
`

enum searchSteps {
  LOCATION = 'location',
  DATE = 'date',
  GUESTS = 'guests',
}

interface Props {
  closeSearch?: () => void;
  onSearch?: () => void;
  search: string;
}

const VillasTabContent = (props: Props) => {
  const { closeSearch, onSearch, search } = props

  const {
    searchItem,
    checkinDate,
    checkoutDate,
    occupancies,
    isFlexibleDateSelected,
    areRoomErrorsShown,
  } = useContext(GlobalSearchStateContext)

  const stepsValidation = useMemo(() => ({
    [searchSteps.LOCATION]: searchItem,
    [searchSteps.DATE]: isFlexibleDateSelected || (checkinDate && checkoutDate),
    [searchSteps.GUESTS]: !areRoomErrorsShown,
  }), [searchItem, checkinDate, checkoutDate, areRoomErrorsShown, isFlexibleDateSelected])

  const [activeStep, setActiveStep] = useState(null)
  const {
    goToNextStep,
    goToPreviousStep,
    closeActiveStep,
    isSearchEnabled,
  } = useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const openLocationSearchStep = useCallback(() => setActiveStep(searchSteps.LOCATION), [setActiveStep])
  const openDateSearchStep = useCallback(() => setActiveStep(searchSteps.DATE), [setActiveStep])
  const openGuestsSearchStep = useCallback(() => setActiveStep(searchSteps.GUESTS), [setActiveStep])

  const { checkin, checkout } = useMemo(() => ({
    checkin: formatDate(checkinDate),
    checkout: formatDate(checkoutDate),
  }), [checkinDate, checkoutDate])

  const guestsDescription = useMemo(() => getOccupancyDescription(occupancies, true, 'property'), [occupancies])
  const HARDCODED_TRENDING_DESTINATIONS = useGlobalVillasAndUniqueStaysTrendingDestinations({ search })
  const dateSearchProps = useDateSearchProps({ onAnytimeDatesSelected: goToNextStep })

  return (
    <ContentWrapper>
      <LocationFloatingPanel
          isOpen={activeStep === searchSteps.LOCATION}
          onClose={closeActiveStep}
          onConfirm={goToNextStep}
          onGoBack={goToPreviousStep}
          typeaheadTypes={UNIQUE_STAYS_TYPEAHEAD_TYPES}
        />

      <DateFloatingPanel
          {...dateSearchProps}
          isOpen={activeStep === searchSteps.DATE}
          onClose={closeActiveStep}
          onConfirm={goToNextStep}
          onGoBack={goToPreviousStep}
          confirmText={isSearchEnabled ? 'Search' : 'Apply'}
        />

      <TravellersFloatingPanel
          isOpen={activeStep === searchSteps.GUESTS}
          onClose={closeActiveStep}
          onConfirm={goToNextStep}
          onGoBack={goToPreviousStep}
          saleUnit="property"
          confirmText={isSearchEnabled ? 'Search' : 'Apply'}
        />

      <FieldsWrapper>
        <SearchFormField
          label="Where"
          placeholder="Search destination, landmark or hotel"
          value={searchItem?.format.mainText}
          onClick={openLocationSearchStep}
        />

        <SearchFormFieldGroup>
          <SearchFormField
            label="Check in"
            placeholder="Anytime"
            value={checkin}
            onClick={openDateSearchStep}
          />
          <SearchFormField
            label="Check out"
            placeholder="Anytime"
            value={checkout}
            onClick={openDateSearchStep}
          />
        </SearchFormFieldGroup>

        <SearchFormField
          label="Guests"
          value={guestsDescription}
          onClick={openGuestsSearchStep}
          hasError={areRoomErrorsShown}
        />
      </FieldsWrapper>

      <PopularSearchesContainer>
        <BodyText variant="medium" colour="neutral-two">
          Popular searches
        </BodyText>
        <PopularSearchesButtonsWrapper>
          <PopularButtonList
            onClickHandler={closeSearch}
            options={HARDCODED_TRENDING_DESTINATIONS}
            fetching={false}
          />
        </PopularSearchesButtonsWrapper>
      </PopularSearchesContainer>
    </ContentWrapper>
  )
}

const mapStateToProps = (state: App.State) => ({
  search: state.router.location.search,
})

export default connect(mapStateToProps)(VillasTabContent)
